// Distance ticks along a straight measurement and the elevation profile helpers, ported from the
// Android app's MeasureTicks.kt.
//
// Android works in dp; the web works in CSS pixels. The formula is the same because Mapbox GL JS,
// like the Android SDK, uses 512 px tiles: 78271.517 m per pixel at the equator at zoom 0.

import { GeoPoint, destination, inverse, normalizeBearing, toRadians } from './geodesy';

/**
 * One tick: where it sits on the line and how far to turn its image (clockwise from north) — the
 * local line bearing − 90, so the app's east-pointing route chevron points along the line.
 */
export interface MeasureTick {
  point: GeoPoint;
  rotation: number;
}

/** The straight line the ticks mark: the geodesic from [start] on [initialBearing] for [distance] m. */
export interface MeasureTickLine {
  start: GeoPoint;
  initialBearing: number;
  distance: number;
}

/** The tick intervals on offer, smallest first: 100 m up to 500 km in 1-2-5 steps. */
export const TICK_INTERVALS_METERS: readonly number[] = [
  100.0, 200.0, 500.0,
  1_000.0, 2_000.0, 5_000.0,
  10_000.0, 20_000.0, 50_000.0,
  100_000.0, 200_000.0, 500_000.0,
];

/** The closest two ticks may sit on screen, in CSS px (Android: dp). */
export const TICK_MIN_SPACING_PX = 40.0;

/** A line never carries more ticks than this; a line that would steps up to a larger interval. */
export const TICK_MAX_COUNT = 1000;

/** The narrowest the range band is drawn: the mockup's `bandW = max(2, 2 · tolerance / S)`. */
export const BAND_MIN_WIDTH_PX = 2.0;

/** Metres per pixel at the equator at zoom 0, for 512 px tiles. */
const METERS_PER_POINT_Z0 = 78_271.517;

/** The smallest tolerance the band is sized for, so a 0 or negative one still gives a finite zoom. */
const BAND_MIN_TOLERANCE_METERS = 0.5;

/** Map metres per screen pixel at [zoom] and [latitude]: 78271.517 · cos φ / 2^z (Android `metersPerDp`). */
export function metersPerPixel(zoom: number, latitude: number): number {
  return METERS_PER_POINT_Z0 * Math.max(Math.cos(toRadians(latitude)), 1e-6) / Math.pow(2.0, zoom);
}

/**
 * The lowest zoom at which [lengthMeters] spans at least [minimumPixels] at [latitude]. Never
 * below 0. (Kotlin's log2 is ln x / ln 2, so that is what this uses.)
 */
export function minZoom(latitude: number, lengthMeters: number, minimumPixels: number): number {
  const cosLat = Math.max(Math.cos(toRadians(latitude)), 1e-6);
  return Math.max(0.0, Math.log(METERS_PER_POINT_Z0 * cosLat * minimumPixels / lengthMeters) / Math.LN2);
}

/**
 * The zoom from which the range band's real width — 2 · [toleranceMeters] — is at least
 * [BAND_MIN_WIDTH_PX] on screen at [latitude]. From here up the annulus fill is drawn; below it a
 * [BAND_MIN_WIDTH_PX] stroke along the distance circle stands in for it. ±5 m at 53°N: z ≈ 13.2.
 */
export function bandFillMinZoom(latitude: number, toleranceMeters: number): number {
  return minZoom(latitude, Math.max(toleranceMeters, BAND_MIN_TOLERANCE_METERS) * 2, BAND_MIN_WIDTH_PX);
}

/**
 * The tick interval at [zoom] for a line starting at [latitude]: the smallest interval whose
 * on-screen spacing is at least [minimumSpacingPx], or the largest when none is.
 */
export function tickInterval(zoom: number, latitude: number, minimumSpacingPx = TICK_MIN_SPACING_PX): number {
  const perPixel = metersPerPixel(zoom, latitude);
  return TICK_INTERVALS_METERS.find((interval) => interval / perPixel >= minimumSpacingPx) ??
    TICK_INTERVALS_METERS[TICK_INTERVALS_METERS.length - 1];
}

/** How many ticks [intervalMeters] puts strictly inside a line [distanceMeters] long. */
export function tickCount(distanceMeters: number, intervalMeters: number): number {
  if (!Number.isFinite(distanceMeters) || !(intervalMeters > 0.0) || distanceMeters <= intervalMeters) return 0;
  const whole = Math.floor(distanceMeters / intervalMeters);
  // The far end itself is not a tick: 300 m at 100 m spacing is two ticks, not three.
  return whole * intervalMeters >= distanceMeters ? whole - 1 : whole;
}

/**
 * [intervalMeters], or the next larger interval until the line needs at most [TICK_MAX_COUNT]
 * ticks (the largest interval when even that needs more).
 */
export function cappedInterval(distanceMeters: number, intervalMeters: number): number {
  let chosen = intervalMeters;
  while (tickCount(distanceMeters, chosen) > TICK_MAX_COUNT) {
    const next = TICK_INTERVALS_METERS.find((interval) => interval > chosen);
    if (next === undefined) return chosen;
    chosen = next;
  }
  return chosen;
}

/**
 * A tick at k · [intervalMeters] from [start] (k ≥ 1) for every such point strictly before the far
 * end, along the geodesic, turned to point along it — at a larger interval when this one would
 * need more than [TICK_MAX_COUNT] ([cappedInterval]).
 */
export function placeTicks(start: GeoPoint, initialBearing: number, distanceMeters: number, intervalMeters: number): MeasureTick[] {
  const interval = cappedInterval(distanceMeters, intervalMeters);
  const count = Math.min(tickCount(distanceMeters, interval), TICK_MAX_COUNT);
  const ticks: MeasureTick[] = [];
  for (let i = 0; i < count; i++) {
    const landed = destination(start, initialBearing, (i + 1) * interval);
    ticks.push({ point: landed.point, rotation: normalizeBearing(landed.finalBearing - 90.0) });
  }
  return ticks;
}

export function placeTicksOnLine(line: MeasureTickLine, intervalMeters: number): MeasureTick[] {
  return placeTicks(line.start, line.initialBearing, line.distance, intervalMeters);
}

const sameLine = (a: MeasureTickLine | null, b: MeasureTickLine | null): boolean =>
  a === b || (a !== null && b !== null &&
    a.start.lat === b.start.lat && a.start.lng === b.start.lng &&
    a.initialBearing === b.initialBearing && a.distance === b.distance);

/**
 * Decides when the tick source needs rebuilding: only when the line or the zoom's interval
 * changed, so a camera that moves every frame re-places the ticks only as it crosses an interval
 * boundary.
 */
export class MeasureTickRefresh {
  private line: MeasureTickLine | null = null;
  private interval = NaN;
  private drawn = false;

  constructor(private readonly minimumSpacingPx = TICK_MIN_SPACING_PX) {}

  /** The ticks to draw for [line] at [zoom], or null when what was last drawn still stands. */
  ticksFor(line: MeasureTickLine | null, zoom: number): MeasureTick[] | null {
    const interval = line ? tickInterval(zoom, line.start.lat, this.minimumSpacingPx) : NaN;
    // NaN never equals itself, so compare "no line" by the line alone.
    if (this.drawn && sameLine(line, this.line) && (line === null || interval === this.interval)) return null;
    this.line = line ? { ...line, start: { ...line.start } } : null;
    this.interval = interval;
    this.drawn = true;
    return line ? placeTicksOnLine(line, interval) : [];
  }

  /** Forgets what was drawn — the source was rebuilt (a style reload) and starts empty. */
  reset(): void {
    this.line = null;
    this.interval = NaN;
    this.drawn = false;
  }
}

/** Samples in the elevation profile. */
export const PROFILE_SAMPLE_COUNT = 50;

/** Evenly spaced points on the geodesic, both ends included. */
export function samplesAlongGeodesic(start: GeoPoint, end: GeoPoint, count = PROFILE_SAMPLE_COUNT): GeoPoint[] {
  const line = inverse(start, end);
  const samples: GeoPoint[] = [];
  for (let i = 0; i < count; i++) {
    if (i === 0) samples.push(start);
    else if (i === count - 1) samples.push(end);
    else samples.push(destination(start, line.initialBearing, line.distance * i / (count - 1)).point);
  }
  return samples;
}

/** Last sample minus first. */
export function change(elevations: number[]): number {
  return elevations.length < 2 ? 0.0 : elevations[elevations.length - 1] - elevations[0];
}

/** The sum of every rise between consecutive samples. */
export function ascent(elevations: number[]): number {
  let total = 0.0;
  for (let i = 1; i < elevations.length; i++) total += Math.max(0.0, elevations[i] - elevations[i - 1]);
  return total;
}

/**
 * The strip's polyline as fractions of its box — x left to right, y down from the top — from the
 * mockup's 56-tall box: y = 52 − (e − min) / max(4, max − min) × 46, i.e. a 6 pt top margin, a 4 pt
 * bottom one, and a flat line for anything under 4 m of relief. Float32, as Android's Pair<Float, Float>.
 */
export function stripPoints(elevations: number[]): Array<[number, number]> {
  if (elevations.length < 2) return [];
  // A loop rather than Math.min(...spread), which overflows the stack on a long route's samples.
  let min = elevations[0];
  let max = elevations[0];
  for (const e of elevations) {
    if (e < min) min = e;
    if (e > max) max = e;
  }
  const range = Math.max(4.0, max - min);
  const last = elevations.length - 1;
  return elevations.map((e, i) => {
    const y56 = 52.0 - (e - min) / range * 46.0;
    return [Math.fround(i / last), Math.fround(y56 / 56.0)];
  });
}
